import { getActivePinia } from 'pinia'

/**
 * Helpers for building `/app/docs/[id]` links that preserve the case or client context the user
 * opened the document from (MAR-1055).
 *
 * `/app/docs/[id].vue` derives its breadcrumb (`Casos › {Caso} › {Documento}`) deterministically
 * from `?caseId=` / `?clientId=` verified against `detail.cases`, never by guessing `detail.cases[0]`
 * when the query parameter is omitted. Every surface inside a case or client — including chat
 * surfaces while scoped to a case — therefore passes the active `caseId` (or `clientId`) on the
 * link.
 */

export interface ScopedDocumentInput {
  caseId?: string | null
  clientId?: string | null
}

export interface ChatDocumentLinkOptions extends ScopedDocumentInput {
  routePath?: string | null
  routeCaseId?: string | null
  activeCaseId?: string | null
  caseDocumentIds?: Iterable<string> | null
  linkedCaseIds?: Iterable<string> | null
}

const CASE_ROUTE_RE = /^\/app\/cases\/([^/?#]+)(?:\/|$)/

/**
 * Build `/app/docs/[id]` carrying `?caseId=` or `?clientId=` when present.
 * Case wins over client when both are supplied (`Cases` is the deeper rung).
 */
export function scopedDocumentHref(docId: string, scope?: ScopedDocumentInput): string {
  const base = `/app/docs/${docId}`
  const caseId = scope?.caseId?.trim()
  if (caseId) return `${base}?caseId=${encodeURIComponent(caseId)}`
  const clientId = scope?.clientId?.trim()
  if (clientId) return `${base}?clientId=${encodeURIComponent(clientId)}`
  return base
}

function caseIdFromPath(path: string | null | undefined): string | null {
  if (!path) return null
  const m = CASE_ROUTE_RE.exec(path)
  const id = m?.[1]?.trim()
  if (!id || id === 'new') return null
  return id
}

function readRoute(): { path?: string; query?: Record<string, unknown> } | null {
  try {
    return typeof useRoute === 'function' ? useRoute() : null
  } catch {
    return null
  }
}

function readWebChatStore(): {
  activeCaseId?: string | null
  activeScope?: { kind?: string; id?: string } | null
} | null {
  try {
    if (typeof useWebChatStore === 'function') return useWebChatStore()
    const pinia = getActivePinia() as { _s?: Map<string, unknown> } | undefined
    return (pinia?._s?.get('web-chat') as {
      activeCaseId?: string | null
      activeScope?: { kind?: string; id?: string } | null
    } | undefined) ?? null
  } catch {
    return null
  }
}

function readCasesStore(): {
  get?: (id: string) => { documentIds?: string[]; document_ids?: string[] } | null
} | null {
  try {
    if (typeof useCasesStore === 'function') return useCasesStore()
    const pinia = getActivePinia() as { _s?: Map<string, unknown> } | undefined
    return (pinia?._s?.get('cases') as {
      get?: (id: string) => { documentIds?: string[]; document_ids?: string[] } | null
    } | undefined) ?? null
  } catch {
    return null
  }
}

function resolveCandidateCaseId(options?: ChatDocumentLinkOptions): string | null {
  if (options?.caseId?.trim()) return options.caseId.trim()

  const route = readRoute()
  const fromPath = caseIdFromPath(options?.routePath ?? route?.path)
  if (fromPath) return fromPath

  const queryCaseId = options?.routeCaseId
    ?? (typeof route?.query?.caseId === 'string' ? route.query.caseId : null)
  if (queryCaseId?.trim()) return queryCaseId.trim()

  if (options?.activeCaseId !== undefined) {
    return options.activeCaseId?.trim() || null
  }

  const webChat = readWebChatStore()
  if (typeof webChat?.activeCaseId === 'string' && webChat.activeCaseId.trim()) {
    return webChat.activeCaseId.trim()
  }
  if (webChat?.activeScope?.kind === 'case' && typeof webChat.activeScope.id === 'string' && webChat.activeScope.id.trim()) {
    return webChat.activeScope.id.trim()
  }

  return null
}

function isDocumentLinkedToCase(docId: string, caseId: string, options?: ChatDocumentLinkOptions): boolean {
  if (options?.linkedCaseIds != null) {
    return new Set(options.linkedCaseIds).has(caseId)
  }
  if (options?.caseDocumentIds != null) {
    return new Set(options.caseDocumentIds).has(docId)
  }

  const record = readCasesStore()?.get?.(caseId)
  const ids = record?.documentIds ?? record?.document_ids
  if (Array.isArray(ids)) {
    return ids.includes(docId)
  }

  // When the cases store has not loaded this case's membership list yet, forward `caseId` so
  // `/app/docs/[id].vue` can validate it against `detail.cases` on arrival.
  return true
}

/**
 * Build a `/app/docs/[id]` href from a chat surface (`ChatDocPreviewPanel`, `UserMessage`, or
 * `open_document` in `dispatch.ts`), appending `?caseId=` when on a `/app/cases/[id]` route or in
 * a case-scoped conversation (`activeCaseId`), provided the document is linked to that case.
 */
export function chatDocumentLink(docId: string, options?: ChatDocumentLinkOptions): string {
  const caseId = resolveCandidateCaseId(options)
  if (caseId && isDocumentLinkedToCase(docId, caseId, options)) {
    return scopedDocumentHref(docId, { caseId })
  }
  if (options?.clientId?.trim()) {
    return scopedDocumentHref(docId, { clientId: options.clientId })
  }
  return `/app/docs/${docId}`
}
